import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import ReactDOM from 'react-dom'
import { Box, Menu, MenuButton, MenuDivider, MenuItem, MenuList, PseudoBox } from '@chakra-ui/core'
import { Check, MoreHorizontal } from 'lucide-react'
import { ButtonVariant, getButtonVariantStyles } from 'components/ui/Button/Button'
import LocaleText from 'components/LocaleText'
import { SelectItem } from '../SelectPopover/SelectItem'

export interface IDropdownOption<T = any> {
  id: string
  label: React.ReactNode
  icon?: React.ReactNode
  isDanger?: boolean
  disabled?: boolean
  sectionTitle?: React.ReactNode
  isDividerBefore?: boolean
  value: T
}

interface IProps<T extends any = any> {
  options: IDropdownOption<T>[]
  onSelect: (option: IDropdownOption<T>) => void
  // Content rendered inside the trigger button (default: three-dots icon)
  triggerContent?: React.ReactNode
  // Which button variant the trigger should look like
  triggerVariant?: ButtonVariant
  // Whether the trigger is in active state (for filter variant)
  triggerIsActive?: boolean
  triggerActiveColor?: string
  // ID of currently selected option — renders a checkmark on it
  selectedId?: string
  selectionIndicator?: 'check' | 'radio'
  // Inset rows use rounded neutral highlights; the default preserves compact edge-to-edge rows.
  itemVariant?: 'default' | 'inset'
  menuWidth?: string | number
  isDisabled?: boolean
  placement?: string
  maxListHeight?: string | number
  // Extra props forwarded to the MenuButton (e.g. w, h, px)
  triggerButtonProps?: Record<string, any>
  // Portal the menu list to <body> so it isn't clipped by scroll/overflow ancestors
  // (grids, virtualized lists, tables with overflow:auto). Default false keeps the in-place Popper.
  // NOTE: portal mode uses a self-managed dropdown — Chakra's own Menu focus logic crashes when its
  // MenuList is portaled (it reads a null focusables ref on open/hover), so we can't just forward usePortal.
  usePortal?: boolean
  // In portal mode, match this element's width and open immediately above it.
  anchorRef?: React.RefObject<HTMLElement>
  menuClassName?: string
  footer?: React.ReactNode
  onOpen?: () => void
}

const ITEM_TRANSITION = 'background 0.1s ease-out, color 0.1s ease-out'
const VIEWPORT_MARGIN = 8
const ROW_HEIGHT = 36
// Caps the list so it always scrolls instead of overflowing the viewport when a caller doesn't
// opt into a specific height (e.g. a per-task subtask count that varies with content).
const DEFAULT_MAX_LIST_HEIGHT = '320px'

const renderLabel = (label: React.ReactNode): React.ReactNode => {
  if (typeof label === 'string') return <LocaleText text={label} />
  return label
}

// Shared option-row content (icon + label + selected check) used by both the Chakra and portaled
// variants, so the two rendering paths can't visually drift.
const OptionInner: React.FC<{
  option: IDropdownOption
  isSelected?: boolean
  selectionIndicator?: 'check' | 'radio'
  itemVariant?: 'default' | 'inset'
}> = ({ option, isSelected, selectionIndicator, itemVariant }) =>
  selectionIndicator === 'radio' ? (
    <SelectItem label={renderLabel(option.label)} isSelected={!!isSelected} indicatorPosition="start" mode="single" />
  ) : (
    <Box display="flex" alignItems="center" w="100%" style={{ gap: '8px' }}>
      {option.icon && (
        <Box
          display="flex"
          alignItems="center"
          flexShrink={0}
          color={option.isDanger || itemVariant === 'inset' ? 'currentColor' : '#64748B'}
        >
          {option.icon}
        </Box>
      )}
      <Box flex={1} textAlign="start">
        {renderLabel(option.label)}
      </Box>
      {isSelected && (
        <Box
          display="flex"
          alignItems="center"
          color={itemVariant === 'inset' ? 'currentColor' : '#64748B'}
          flexShrink={0}
        >
          <Check size={14} />
        </Box>
      )}
    </Box>
  )

// Shared list styling for both the Chakra and portaled variants.
const LIST_SHADOW = '0 4px 24px rgba(15,23,42,0.10), 0 1px 4px rgba(15,23,42,0.06)'

// Portaled dropdown: a self-managed trigger + a body-portaled list positioned from the trigger box.
// Immune to ancestor `overflow` clipping (grids, virtualized lists, scroll tables).
function PortaledDropdown<T>({
  options,
  onSelect,
  triggerContent,
  triggerVariant = 'icon',
  triggerIsActive,
  triggerActiveColor,
  selectedId,
  selectionIndicator,
  itemVariant = 'default',
  menuWidth = 200,
  isDisabled,
  placement = 'bottom-start',
  maxListHeight = DEFAULT_MAX_LIST_HEIGHT,
  triggerButtonProps,
  anchorRef,
  menuClassName,
  footer,
  onOpen,
}: IProps<T>) {
  const triggerRef = useRef<HTMLElement>(null)
  const menuRef = useRef<HTMLDivElement>(null)
  const initialFocus = useRef<'first' | 'last'>('first')
  const [isOpen, setIsOpen] = useState(false)
  const [position, setPosition] = useState({ top: 0, left: 0, width: 200, maxHeight: 320 })

  const triggerStyles = getButtonVariantStyles(triggerVariant, triggerIsActive, triggerActiveColor)
  const isInset = itemVariant === 'inset'
  const itemBackground = isInset ? '#F1F1F1' : '#F8FAFC'
  const itemFocusBackground = isInset ? '#F1F1F1' : '#F1F5F9'
  const width = typeof menuWidth === 'number' ? menuWidth : parseInt(`${menuWidth}`, 10) || 200
  const alignEnd = placement.includes('end')
  const preferAbove = placement.startsWith('top')

  const computePosition = useCallback(() => {
    const rect = (anchorRef?.current || triggerRef.current)?.getBoundingClientRect()
    if (!rect) return
    const capHeight = typeof maxListHeight === 'number' ? maxListHeight : parseInt(`${maxListHeight}`, 10) || 320
    const viewportWidth = Math.max(0, window.innerWidth - VIEWPORT_MARGIN * 2)
    const menuWidth = Math.min(anchorRef ? rect.width : width, viewportWidth)
    const gap = anchorRef ? 8 : 4
    const availableAbove = Math.max(0, rect.top - gap - VIEWPORT_MARGIN)
    const maxHeight = Math.min(capHeight, anchorRef ? availableAbove : window.innerHeight - VIEWPORT_MARGIN * 2)
    const contentHeight = menuRef.current ? menuRef.current.scrollHeight + 2 : options.length * ROW_HEIGHT + 8
    const height = Math.min(contentHeight, maxHeight)
    let top = rect.bottom + 4
    if (anchorRef || (preferAbove && availableAbove >= height) || top + height > window.innerHeight - VIEWPORT_MARGIN) {
      top = Math.max(VIEWPORT_MARGIN, rect.top - height - gap)
    }
    const left = Math.min(
      Math.max(!anchorRef && alignEnd ? rect.right - menuWidth : rect.left, VIEWPORT_MARGIN),
      window.innerWidth - menuWidth - VIEWPORT_MARGIN
    )
    setPosition((current) =>
      current.top === top && current.left === left && current.width === menuWidth && current.maxHeight === maxHeight
        ? current
        : { top, left, width: menuWidth, maxHeight }
    )
  }, [anchorRef, maxListHeight, options.length, width, alignEnd, preferAbove])

  const open = (focus: 'first' | 'last' = 'first') => {
    if (isDisabled) return
    initialFocus.current = focus
    computePosition()
    setIsOpen(true)
    onOpen?.()
  }

  const close = (restoreFocus = false) => {
    setIsOpen(false)
    if (restoreFocus) triggerRef.current?.focus()
  }

  const enabledItems = () =>
    Array.from(menuRef.current?.querySelectorAll<HTMLButtonElement>('[role^="menuitem"]:not(:disabled)') || [])

  useLayoutEffect(() => {
    if (!isOpen) return
    computePosition()
    const items = enabledItems()
    const item = initialFocus.current === 'last' ? items[items.length - 1] : items[0]
    const focusTarget = item || menuRef.current
    focusTarget?.focus()
  }, [isOpen, computePosition])

  useEffect(() => {
    if (!isOpen || typeof ResizeObserver === 'undefined') return undefined
    const observer = new ResizeObserver(computePosition)
    if (menuRef.current) observer.observe(menuRef.current)
    if (anchorRef?.current) observer.observe(anchorRef.current)
    return () => observer.disconnect()
  }, [isOpen, anchorRef, computePosition])

  useEffect(() => {
    if (!isOpen) return undefined
    const onDocMouseDown = (e: MouseEvent) => {
      const target = e.target as Node
      if (menuRef.current?.contains(target) || triggerRef.current?.contains(target)) return
      close()
    }
    const onScroll = (e: Event) => {
      if (e.target instanceof Node && menuRef.current?.contains(e.target)) return
      if (anchorRef) computePosition()
      else close()
    }
    const onResize = () => (anchorRef ? computePosition() : close())
    const onFocusIn = (e: FocusEvent) => {
      const target = e.target as Node
      if (!menuRef.current?.contains(target) && !triggerRef.current?.contains(target)) close()
    }
    document.addEventListener('mousedown', onDocMouseDown)
    document.addEventListener('focusin', onFocusIn)
    window.addEventListener('scroll', onScroll, true)
    window.addEventListener('resize', onResize)
    return () => {
      document.removeEventListener('mousedown', onDocMouseDown)
      document.removeEventListener('focusin', onFocusIn)
      window.removeEventListener('scroll', onScroll, true)
      window.removeEventListener('resize', onResize)
    }
  }, [isOpen, anchorRef, computePosition])

  const onMenuKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Escape') {
      event.preventDefault()
      event.stopPropagation()
      close(true)
      return
    }
    if (event.key === 'Tab') {
      // Resume native tab order from the trigger, instead of the portal at the end of <body>.
      close(true)
      return
    }
    const items = enabledItems()
    if (!items.length) return
    const current = items.indexOf(document.activeElement as HTMLButtonElement)
    let next: number
    switch (event.key) {
      case 'ArrowDown':
        next = (current + 1) % items.length
        break
      case 'ArrowUp':
        next = current < 0 ? items.length - 1 : (current - 1 + items.length) % items.length
        break
      case 'Home':
        next = 0
        break
      case 'End':
        next = items.length - 1
        break
      default:
        return
    }
    event.preventDefault()
    event.stopPropagation()
    items[next].focus()
  }

  const pick = (e: React.MouseEvent, option: IDropdownOption<T>) => {
    e.stopPropagation()
    if (option.disabled) return
    close(true)
    onSelect(option)
  }

  return (
    <Box display="inline-flex" onClick={(e: React.MouseEvent) => e.stopPropagation()}>
      <PseudoBox
        as="button"
        ref={triggerRef as any}
        borderRadius="8px"
        fontWeight="500"
        fontSize="14px"
        transition="all 0.15s ease-out"
        display="inline-flex"
        alignItems="center"
        aria-disabled={isDisabled}
        aria-haspopup="menu"
        aria-expanded={isOpen}
        {...(triggerStyles as any)}
        {...(triggerButtonProps as any)}
        type="button"
        disabled={isDisabled}
        onClick={(event: React.MouseEvent) => {
          triggerButtonProps?.onClick?.(event)
          if (event.defaultPrevented) return
          event.stopPropagation()
          if (isOpen) close(true)
          else open()
        }}
        onKeyDown={(event: React.KeyboardEvent) => {
          triggerButtonProps?.onKeyDown?.(event)
          if (event.defaultPrevented) return
          if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
            event.preventDefault()
            event.stopPropagation()
            open(event.key === 'ArrowUp' ? 'last' : 'first')
          } else if (event.key === 'Escape' && isOpen) {
            event.preventDefault()
            event.stopPropagation()
            close(true)
          }
        }}
      >
        {triggerContent ?? <MoreHorizontal size={16} />}
      </PseudoBox>

      {isOpen &&
        ReactDOM.createPortal(
          <Box
            ref={menuRef}
            className={menuClassName}
            role="menu"
            dir={triggerRef.current ? getComputedStyle(triggerRef.current).direction : undefined}
            aria-label={triggerButtonProps?.['aria-label'] || 'Actions'}
            tabIndex={-1}
            position="fixed"
            top={`${position.top}px`}
            left={`${position.left}px`}
            minWidth={`${position.width}px`}
            width={anchorRef ? `${position.width}px` : undefined}
            maxWidth="calc(100vw - 16px)"
            maxH={`${position.maxHeight}px`}
            overflowY="auto"
            bg="white"
            borderRadius="10px"
            boxShadow={LIST_SHADOW}
            borderWidth="1px"
            borderStyle="solid"
            borderColor="#E2E8F0"
            py="4px"
            zIndex={4000}
            onClick={(e: React.MouseEvent) => e.stopPropagation()}
            onKeyDown={onMenuKeyDown}
          >
            {options.map((option) => {
              const isSelected = option.id === selectedId
              return (
                <React.Fragment key={option.id}>
                  {option.sectionTitle && <Box role="presentation">{option.sectionTitle}</Box>}
                  {option.isDividerBefore && <Box my="4px" height="1px" bg="#F1F5F9" />}
                  <PseudoBox
                    as="button"
                    {...({ type: 'button', disabled: option.disabled } as any)}
                    role={selectionIndicator === 'radio' ? 'menuitemradio' : 'menuitem'}
                    aria-checked={selectionIndicator === 'radio' ? isSelected : undefined}
                    tabIndex={-1}
                    aria-disabled={option.disabled}
                    display="flex"
                    width={isInset ? 'calc(100% - 8px)' : '100%'}
                    mx={isInset ? '4px' : undefined}
                    borderRadius={isInset ? '10px' : undefined}
                    alignItems="center"
                    color={option.isDanger ? '#DC2626' : '#0F172A'}
                    fontSize="12px"
                    fontWeight={isSelected ? 600 : 500}
                    px="10px"
                    py="7px"
                    cursor={option.disabled ? 'not-allowed' : 'pointer'}
                    opacity={option.disabled ? 0.45 : 1}
                    background={isSelected ? itemBackground : 'transparent'}
                    transition={ITEM_TRANSITION}
                    _hover={{ background: option.isDanger ? '#FEF2F2' : itemBackground }}
                    _focus={{ background: option.isDanger ? '#FEF2F2' : itemFocusBackground, outline: 'none' }}
                    onPointerMove={(event: React.PointerEvent<HTMLButtonElement>) => {
                      if (!option.disabled && document.activeElement !== event.currentTarget) {
                        event.currentTarget.focus({ preventScroll: true })
                      }
                    }}
                    onClick={(e: React.MouseEvent) => pick(e, option)}
                  >
                    <OptionInner
                      option={option}
                      isSelected={isSelected}
                      selectionIndicator={selectionIndicator}
                      itemVariant={itemVariant}
                    />
                  </PseudoBox>
                </React.Fragment>
              )
            })}
            {footer && <Box role="presentation">{footer}</Box>}
          </Box>,
          document.body
        )}
    </Box>
  )
}

export const DropdownMenu = <T extends any = any>(props: IProps<T>) => {
  const {
    options,
    onSelect,
    triggerContent,
    triggerVariant = 'icon',
    triggerIsActive,
    triggerActiveColor,
    selectedId,
    selectionIndicator,
    itemVariant = 'default',
    menuWidth = 200,
    isDisabled,
    placement = 'bottom-start',
    maxListHeight = DEFAULT_MAX_LIST_HEIGHT,
    triggerButtonProps,
    usePortal = false,
    menuClassName,
    footer,
    onOpen,
  } = props

  // Portal mode needs a self-managed dropdown (Chakra's Menu focus logic crashes when portaled).
  if (usePortal) return <PortaledDropdown<T> {...props} />

  const triggerStyles = getButtonVariantStyles(triggerVariant, triggerIsActive, triggerActiveColor)
  const isInset = itemVariant === 'inset'
  const itemBackground = isInset ? '#F1F1F1' : '#F8FAFC'
  const itemFocusBackground = isInset ? '#F1F1F1' : '#F1F5F9'

  return (
    <Menu {...({ onOpen } as any)}>
      <MenuButton
        borderRadius="8px"
        fontWeight="500"
        fontSize="14px"
        transition="all 0.15s ease-out"
        display="inline-flex"
        alignItems="center"
        isDisabled={isDisabled}
        {...(triggerStyles as any)}
        {...(triggerButtonProps as any)}
      >
        {triggerContent ?? <MoreHorizontal size={16} />}
      </MenuButton>

      <MenuList
        className={menuClassName}
        placement={placement as any}
        minWidth={menuWidth}
        maxH={maxListHeight}
        borderRadius="10px"
        boxShadow={LIST_SHADOW}
        borderWidth="1px"
        borderStyle="solid"
        borderColor="#E2E8F0"
        py="4px"
        zIndex={2000}
        overflow="auto"
      >
        {options.map((option) => {
          const isSelected = option.id === selectedId
          return (
            <React.Fragment key={option.id}>
              {option.sectionTitle && <Box role="presentation">{option.sectionTitle}</Box>}
              {option.isDividerBefore && <MenuDivider my="4px" borderColor="#F1F5F9" />}
              <MenuItem
                role={selectionIndicator === 'radio' ? 'menuitemradio' : 'menuitem'}
                aria-checked={selectionIndicator === 'radio' ? isSelected : undefined}
                isDisabled={option.disabled}
                onClick={() => !option.disabled && onSelect(option)}
                width={isInset ? 'calc(100% - 8px)' : '100%'}
                mx={isInset ? '4px' : undefined}
                borderRadius={isInset ? '10px' : undefined}
                color={option.isDanger ? '#DC2626' : '#0F172A'}
                fontSize="12px"
                fontWeight={isSelected ? '600' : '500'}
                px="10px"
                py="7px"
                cursor={option.disabled ? 'not-allowed' : 'pointer'}
                opacity={option.disabled ? 0.45 : 1}
                bg={isSelected ? itemBackground : undefined}
                transition={ITEM_TRANSITION}
                _hover={{
                  bg: option.isDanger ? '#FEF2F2' : itemBackground,
                  color: option.isDanger ? '#B91C1C' : '#0F172A',
                }}
                _focus={{ bg: option.isDanger ? '#FEF2F2' : itemFocusBackground, outline: 'none' }}
              >
                <OptionInner
                  option={option}
                  isSelected={isSelected}
                  selectionIndicator={selectionIndicator}
                  itemVariant={itemVariant}
                />
              </MenuItem>
            </React.Fragment>
          )
        })}
        {footer && <Box role="presentation">{footer}</Box>}
      </MenuList>
    </Menu>
  )
}
